import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect } from 'react';
import { View, ActivityIndicator, TextInput, TouchableOpacity } from 'react-native';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { apiRequest } from '../../services/apiClient';
import { brand } from '../../design-system/theme/tokens';

interface AIPolicyData {
  id: string;
  school_id: number | null;
  grading_harshness: number;
  narrative_style: string;
  feedback_detail_level: string;
  knowledge_pack_ids: string[];
  framework_source_ids: string[];
  updated_at: string | null;
}

export interface AIPolicyEditorProps {
  mode: 'platform' | 'school';
  schoolId?: number;          // required when mode='school'
  title: string;              // e.g. "Platform Default Policy" or "School AI Policy"
}

export default function AIPolicyEditor({ mode, schoolId, title }: AIPolicyEditorProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState<AIPolicyData | null>(null);
  const [harshness, setHarshness] = useState('0.5');
  const [narrativeStyle, setNarrativeStyle] = useState('balanced');
  const [detailLevel, setDetailLevel] = useState('standard');
  const [result, setResult] = useState<string | null>(null);

  const basePath = mode === 'platform'
    ? '/admin/ai-policy/platform'
    : `/admin/ai-policy/schools/${schoolId}`;

  useEffect(() => {
    setLoading(true);
    apiRequest<AIPolicyData>({ endpoint: basePath, method: 'GET' })
      .then((data: AIPolicyData) => {
        setPolicy(data);
        setHarshness(String(data.grading_harshness));
        setNarrativeStyle(data.narrative_style);
        setDetailLevel(data.feedback_detail_level);
      })
      .catch((e: Error) => {
        // No policy row exists yet (first-time setup): seed an editable
        // default so the admin can create it by saving, instead of surfacing
        // a "not found" error. Any other failure is a real error.
        if (/not found/i.test(e.message)) {
          setPolicy({ knowledge_pack_ids: [], framework_source_ids: [] } as unknown as AIPolicyData);
        } else {
          showDialog('Error', e.message);
        }
      })
      .finally(() => setLoading(false));
  }, [basePath]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiRequest({
        endpoint: basePath,
        method: 'PUT',
        data: {
          grading_harshness: parseFloat(harshness),
          narrative_style: narrativeStyle,
          feedback_detail_level: detailLevel,
          knowledge_pack_ids: policy?.knowledge_pack_ids ?? [],
          framework_source_ids: policy?.framework_source_ids ?? [],
        },
      });
      setResult('Policy saved successfully');
    } catch (e: unknown) {
      showDialog('Error', (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <Text className="font-semibold text-lg">{title}</Text>
      </CardHeader>
      <CardContent>
        {loading ? (
          <View className="items-center justify-center py-6">
            <ActivityIndicator size="small" color={brand.primary} />
          </View>
        ) : (
          <View className="gap-4">
            {/* Grading Harshness */}
            <View>
              <Text className="text-sm font-medium text-gray-700 mb-1">
                Grading Harshness (0.0 – 1.0)
              </Text>
              <TextInput
                value={harshness}
                onChangeText={setHarshness}
                placeholder="0.5"
                keyboardType="decimal-pad"
                autoCapitalize="none"
                autoCorrect={false}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <Text className="text-xs text-gray-500 mt-1">
                0 = most lenient, 1 = most strict
              </Text>
            </View>

            {/* Narrative Style */}
            <View>
              <Text className="text-sm font-medium text-gray-700 mb-2">
                Narrative Style
              </Text>
              <View className="flex-row gap-2">
                {(['balanced', 'encouraging', 'direct'] as const).map((style) => (
                  <TouchableOpacity
                    key={style}
                    onPress={() => setNarrativeStyle(style)}
                    className={`flex-1 py-2 px-2 rounded-full border ${
                      narrativeStyle === style
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-gray-100 border-gray-200'
                    }`}
                  >
                    <Text
                      className={`text-xs text-center font-medium ${
                        narrativeStyle === style ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      {style.charAt(0).toUpperCase() + style.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Detail Level */}
            <View>
              <Text className="text-sm font-medium text-gray-700 mb-2">
                Feedback Detail Level
              </Text>
              <View className="flex-row gap-2">
                {(['brief', 'standard', 'detailed'] as const).map((level) => (
                  <TouchableOpacity
                    key={level}
                    onPress={() => setDetailLevel(level)}
                    className={`flex-1 py-2 px-2 rounded-full border ${
                      detailLevel === level
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-gray-100 border-gray-200'
                    }`}
                  >
                    <Text
                      className={`text-xs text-center font-medium ${
                        detailLevel === level ? 'text-white' : 'text-gray-700'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Updated at */}
            {policy?.updated_at && (
              <Text className="text-xs text-gray-400">
                Last updated: {new Date(policy.updated_at).toLocaleString()}
              </Text>
            )}

            {/* Save Button */}
            <Button onPress={handleSave} disabled={saving} className="mt-2">
              <Text className="text-white font-medium">
                {saving ? 'Saving...' : 'Save Policy'}
              </Text>
            </Button>

            {/* Result */}
            {result && (
              <Text className="text-sm text-green-600 font-medium">{result}</Text>
            )}
          </View>
        )}
      </CardContent>
    </Card>
  );
}
